import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useRole } from '@/hooks/useRole';
import { Plus, FileText, Download, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { POUploadForm } from '@/components/admin/POUploadForm';
import { POManagement } from '@/components/admin/POManagement';
import { PublicSubmissionsManager } from '@/components/admin/PublicSubmissionsManager';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function PurchaseOrders() {
  const { user } = useAuth();
  const { isAdmin } = useRole();
  const { toast } = useToast();
  const [pos, setPOs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [isMattMonjan, setIsMattMonjan] = useState(false);

  useEffect(() => {
    if (user) {
      const isMatt = user.email === 'matt.monjan@infusedu.com' || user.email === 'matt.monjan@infusesdu.com';
      setIsMattMonjan(isMatt);
      loadPOs(isMatt);
    }
  }, [user]);

  const loadPOs = async (isMatt?: boolean) => {
    try {
      // For uber admin (Matt Monjan), load all POs
      // For district admins, load POs for their district
      let query = supabase
        .from('purchase_orders')
        .select(`
          *,
          po_line_items(*)
        `);

      // Use the passed parameter or the state
      const isUberAdmin = isMatt !== undefined ? isMatt : isMattMonjan;

      // If not uber admin, filter by district
      if (!isUberAdmin) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('district_id')
          .eq('id', user?.id)
          .single();
        
        if (profile?.district_id) {
          query = query.eq('district_id', profile.district_id);
        }
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      setPOs(data || []);
    } catch (error) {
      console.error('Error loading POs:', error);
      toast({
        title: "Error",
        description: "Failed to load purchase orders",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePOUploaded = () => {
    setShowUploadDialog(false);
    loadPOs();
    toast({
      title: "Success",
      description: "Purchase order uploaded successfully",
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading purchase orders...</div>
        </div>
      </div>
    );
  }

  console.log('Debug - User role info:', { isAdmin, isMattMonjan, userEmail: user?.email });

  if (!isAdmin && !isMattMonjan) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              You don't have permission to view purchase orders. 
              Admin status: {isAdmin ? 'Yes' : 'No'}, 
              Matt Monjan: {isMattMonjan ? 'Yes' : 'No'},
              Email: {user?.email}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Purchase Orders</h1>
          <p className="text-muted-foreground">
            {isMattMonjan 
              ? "Manage district purchase orders and create invoices"
              : "Upload and manage your district's purchase orders"
            }
          </p>
        </div>
        {isAdmin && (
          <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
            <DialogTrigger asChild>
                       <Button>
                        <Plus className="h-4 w-4 mr-2" />
                        Upload Purchase Order
                      </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Upload Purchase Order</DialogTitle>
              </DialogHeader>
              <POUploadForm onSuccess={handlePOUploaded} />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {isMattMonjan ? (
        <Tabs defaultValue="pos" className="space-y-4">
          <TabsList>
            <TabsTrigger value="pos">Purchase Orders</TabsTrigger>
            <TabsTrigger value="submissions">Public Submissions (Matt Monjan Only)</TabsTrigger>
          </TabsList>
          <TabsContent value="pos">
            <POManagement pos={pos} onUpdate={loadPOs} />
          </TabsContent>
          <TabsContent value="submissions">
            <PublicSubmissionsManager onUpdate={loadPOs} />
          </TabsContent>
        </Tabs>
      ) : isAdmin ? (
        <POManagement pos={pos} onUpdate={loadPOs} />
      ) : (
        <div className="grid gap-6">
          {pos.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Purchase Orders</h3>
                  <p className="text-muted-foreground mb-4">
                    Upload your first purchase order to get started.
                  </p>
                  <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
                    <DialogTrigger asChild>
                      <Button>
                        <Plus className="h-4 w-4 mr-2" />
                        Upload PO
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Upload Purchase Order</DialogTitle>
                      </DialogHeader>
                      <POUploadForm onSuccess={handlePOUploaded} />
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>
          ) : (
            pos.map((po) => (
              <Card key={po.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>PO #{po.po_number}</CardTitle>
                      <CardDescription>
                        {new Date(po.po_date).toLocaleDateString()} • ${po.total_amount.toFixed(2)}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      {po.po_file_url && (
                        <Button variant="outline" size="sm" asChild>
                          <a href={po.po_file_url} target="_blank" rel="noopener noreferrer">
                            <Eye className="h-4 w-4 mr-2" />
                            View File
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold mb-2">Bill To</h4>
                      <p className="text-sm text-muted-foreground">
                        {po.bill_to_name}<br />
                        {po.bill_to_address}<br />
                        {po.bill_to_city}, {po.bill_to_state} {po.bill_to_zip}
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Status</h4>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          po.invoice_created 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {po.invoice_created ? 'Invoice Created' : 'Pending'}
                        </span>
                      </div>
                    </div>
                  </div>
                  {po.comments && (
                    <div className="mt-4">
                      <h4 className="font-semibold mb-2">Comments</h4>
                      <p className="text-sm text-muted-foreground">{po.comments}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
}